
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Portfolio, PortfolioType } from "@/types/portfolio";
import { Plus } from "lucide-react";

interface AddItemDialogProps {
  portfolio: Portfolio;
  onAdd: (item: { name: string; description: string | null }) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const AddItemDialog = ({ portfolio, onAdd, open, onOpenChange }: AddItemDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Use the external open state if provided, otherwise use internal state
  const isOpen = open !== undefined ? open : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const getItemTypeName = (portfolioType: PortfolioType): string => {
    switch (portfolioType) {
      case "buildings":
        return "Building";
      case "formulated_products":
        return "Product";
      case "interior_spaces":
        return "Space";
      case "paper_rolls":
        return "Paper Roll";
      default:
        return "Item";
    }
  };

  const itemType = getItemTypeName(portfolio.portfolio_type);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onAdd({
        name: name.trim(),
        description: description.trim() || null,
      });
      setName("");
      setDescription("");
      setOpen(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {!onOpenChange && (
        <DialogTrigger asChild>
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            Add {itemType}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add New {itemType}</DialogTitle>
            <DialogDescription>
              Enter the details for the new {itemType.toLowerCase()} to add it to your portfolio.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{itemType} Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`Enter ${itemType.toLowerCase()} name`}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter a description (optional)"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit">Add {itemType}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
